import React from "react";
import { Box, Chip, Link, Typography } from "@mui/material";
import { userLifecycleFromAutomatableRecord } from "../utils/ticketLifecycleClassification";

export const getTicketTheoUrl = (ticketId: string | number) =>
  `${window.location.origin}/supervisor/tickets/${encodeURIComponent(String(ticketId))}`;

/** Matches `SupervisorTicketDetail` / NeoAssistant `prefillPrompt` → chat input starter text. */
export const THEO_TICKET_EXECUTE_PREFILL = "Please handle this ticket.";

export const getTicketTheoExecutePath = (ticketId: string | number) => {
  const params = new URLSearchParams({ prefillPrompt: THEO_TICKET_EXECUTE_PREFILL });
  return `/supervisor/tickets/${encodeURIComponent(String(ticketId))}?${params.toString()}`;
};

export const getTicketTheoExecuteUrl = (ticketId: string | number) =>
  `${window.location.origin}${getTicketTheoExecutePath(ticketId)}`;

export const truncateText = (value: string | undefined, maxLength = 500) => {
  const text = String(value || "").trim();
  if (!text) return "-";
  return text.length > maxLength ? `${text.slice(0, maxLength).trim()}...` : text;
};

/**
 * Reads dashboard URL query `cacheDay` / `automatableCacheDay` (UTC yyyy-mm-dd) for
 * ``POST /api/msp-analytics/dashboard-automatable`` body ``cache_day``.
 */
export function parseDashboardCacheDayParam(value: string | null | undefined): string | null {
  if (value == null || !String(value).trim()) return null;
  const s = String(value).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const t = Date.parse(`${s}T12:00:00.000Z`);
  if (Number.isNaN(t)) return null;
  return s;
}

/**
 * Reads dashboard URL query ``legacyAutomatable`` / ``automatableLegacy`` / ``compat`` — restores viewing
 * cached reports keyed only on ``current_theo`` (no ``dashboard_automatable_eligible`` / step-coverage gate).
 */
export function parseLegacyAutomatableParam(value: string | null | undefined): boolean {
  if (value == null || !String(value).trim()) return false;
  return ["1", "true", "yes", "legacy"].includes(String(value).trim().toLowerCase());
}

export const getExcerptValue = (excerpt: string | undefined, keys: string[]) => {
  const lines = String(excerpt || "").split("\n");
  const normalizedKeys = keys.map((key) => key.toLowerCase());
  for (const line of lines) {
    const separatorIndex = line.indexOf(":");
    if (separatorIndex < 0) continue;
    const key = line.slice(0, separatorIndex).trim().toLowerCase();
    if (normalizedKeys.some((candidate) => key === candidate || key.endsWith(`.${candidate}`))) {
      return line.slice(separatorIndex + 1).trim();
    }
  }
  return "";
};

const formatStepsSegments = (segments: string[]) => {
  const lines = segments.map((s) => s.trim()).filter(Boolean);
  if (lines.length === 0) return "";
  if (lines.length === 1) return lines[0]!;
  return lines.map((line, idx) => `${idx + 1}. ${line}`).join("\n");
};

/** Parse Python-ish `['a', 'b']` when JSON.parse fails (single quotes). */
const parseBracketQuotedList = (s: string): string[] | null => {
  const t = s.trim();
  if (!t.startsWith("[") || !t.endsWith("]")) return null;
  const inner = t.slice(1, -1);
  const out: string[] = [];
  let i = 0;

  while (i < inner.length) {
    while (i < inner.length && /\s/.test(inner[i]!)) i++;
    if (i >= inner.length) break;

    const q = inner[i]!;
    if (q !== "'" && q !== '"') return null;

    i += 1;
    let buf = "";
    while (i < inner.length) {
      const ch = inner[i]!;
      if (ch === "\\" && q === "'" && i + 1 < inner.length) {
        buf += inner[i + 1]!;
        i += 2;
        continue;
      }
      if (ch === "\\" && q === '"' && i + 1 < inner.length) {
        buf += inner[i + 1]!;
        i += 2;
        continue;
      }
      if (ch === q) {
        i += 1;
        out.push(buf);
        break;
      }
      buf += ch;
      i += 1;
    }

    while (i < inner.length && /\s/.test(inner[i]!)) i++;
    if (i >= inner.length) break;
    if (inner[i] !== ",") return null;
    i += 1;
  }

  return out;
};

export const normalizeStepsPayload = (raw: unknown): string => {
  const fromNestedArray = (arr: unknown[]): string => {
    const flat: string[] = [];
    const walk = (v: unknown) => {
      if (Array.isArray(v)) v.forEach(walk);
      else if (v !== null && v !== undefined && String(v).trim() !== "") {
        flat.push(String(v).trim());
      }
    };
    arr.forEach(walk);
    return formatStepsSegments(flat);
  };

  if (raw === null || raw === undefined) return "";
  if (Array.isArray(raw)) return fromNestedArray(raw);

  const text = String(raw).trim();
  if (!text) return "";

  if (text.startsWith("[")) {
    try {
      const parsed = JSON.parse(text) as unknown;
      if (Array.isArray(parsed)) return fromNestedArray(parsed);
      if (typeof parsed === "string" && parsed.trim()) return normalizeStepsPayload(parsed);
    } catch {
      /* not JSON double-quoted */
    }
    const bracket = parseBracketQuotedList(text);
    if (bracket !== null) return formatStepsSegments(bracket);
  }

  return text;
};

const getStepsTheoCanTake = (record: any) => {
  const direct = normalizeStepsPayload(record?.analysis?.steps_theo_can_take);
  if (direct) return direct;
  const legacy = String(record?.analysis?.evidence || "").trim();
  if (legacy) return legacy;
  const reason = String(record?.analysis?.primary_reason || "").trim();
  return reason || "-";
};

/** True when LLM/source ``ticket_type`` is user onboarding / offboarding (analytics + dashboard). */
export function isUserOnboardingOrOffboardingAutomatableTicket(record: any): boolean {
  return userLifecycleFromAutomatableRecord(record) !== "none";
}

const CUSTOM_WORKFLOWS_APP_PATH = "/custom_workflows";

/** Per-line mapping from analytics (`build_step_theo_action_bindings`). */
export type StepTheoActionBinding = {
  tag: string;
  step_text: string;
  resolved_actions: string[];
  resolution_note: string | null;
};

/** Minutes per tagged step type (heuristic for "time saved" display). */
const STEP_TAG_MINUTES: Record<string, number> = {
  gather: 3,
  validate: 3,
  verify: 3,
  action: 5,
  notify: 1,
  document: 3,
};

const normalizeStepTagKey = (inner: string) =>
  inner
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const estimatedMinutesSavedFromStepsText = (stepsText: string): number => {
  if (!stepsText || stepsText === "-") return 0;
  let total = 0;
  for (const line of stepsText.split(/\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const withoutNum = trimmed.replace(/^\s*\d+\.\s*/, "").trim();
    const tagMatch = withoutNum.match(/^\[([^\]]+)\]/);
    if (!tagMatch) continue;
    const key = normalizeStepTagKey(tagMatch[1]!);
    const minutes = STEP_TAG_MINUTES[key];
    if (minutes != null) total += minutes;
  }
  return total;
};

/** One "action" per `[Tag]` step line in `steps_theo_can_take` (same lines as the time-saved heuristic). */
export const countTheoAutomatableStepActions = (record: any): number => {
  const stepsText = getStepsTheoCanTake(record);
  if (!stepsText || stepsText === "-") return 0;
  let n = 0;
  for (const line of stepsText.split(/\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const withoutNum = trimmed.replace(/^\s*\d+\.\s*/, "").trim();
    if (withoutNum.match(/^\[[^\]]+\]/)) n += 1;
  }
  return n;
};

/** Uses the same step source as the Actions column; sums minutes per [Gather]/[Validate]/… tag. */
export const getEstimatedTimeSavedMinutes = (record: any) =>
  estimatedMinutesSavedFromStepsText(getStepsTheoCanTake(record));

export const formatTimeSavedEstimate = (minutes: number) => {
  if (minutes <= 0) return "—";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

/** Row-sum total (same heuristic as per-ticket "Time saved") → readable hours or minutes for the dashboard blurb. */
export const formatAggregateTimeSavedForSummary = (totalMinutes: number): string => {
  if (totalMinutes <= 0) return "0 hours";
  const hours = totalMinutes / 60;
  if (hours < 1) {
    const m = Math.round(totalMinutes);
    return `${m} minute${m === 1 ? "" : "s"}`;
  }
  const rounded = Math.round(hours * 10) / 10;
  const label = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${label} hour${rounded === 1 ? "" : "s"}`;
};

export const AutomatableTheoExecuteLink: React.FC<{ record: any }> = ({ record }) => {
  const ticket = record?.ticket || {};
  const ticketId = ticket.ticket_id ?? ticket.id;
  if (ticketId === undefined || ticketId === null || String(ticketId).trim() === "") {
    return null;
  }

  const isM365Ticket = Boolean(record?.analysis?.is_m365);
  const m365Avail = record?.analysis?.m365_integration_available;
  const missingM365Integration = isM365Ticket && m365Avail === false;
  const isGWorkspaceTicket = Boolean(record?.analysis?.is_gworkspace);
  const gwAvail = record?.analysis?.gworkspace_integration_available;
  const missingGWorkspaceIntegration = isGWorkspaceTicket && gwAvail === false;

  return (
    <Box
      sx={{
        mb: 1.25,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "baseline",
        gap: 0.5,
        columnGap: 0.75,
        minWidth: 0,
      }}
    >
      <Link
        href={getTicketTheoExecuteUrl(ticketId)}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{ fontWeight: 600, fontSize: "0.8125rem" }}
      >
        Execute with Theo
      </Link>
      {missingM365Integration ? (
        <Typography component="span" variant="body2" sx={{ fontWeight: 400, fontSize: "0.8125rem" }}>
          (Missing M365 integration, will not fully execute)
        </Typography>
      ) : null}
      {missingGWorkspaceIntegration ? (
        <Typography component="span" variant="body2" sx={{ fontWeight: 400, fontSize: "0.8125rem" }}>
          (Missing Google Workspace integration, will not fully execute)
        </Typography>
      ) : null}
    </Box>
  );
};

export type AutomatableActionsCellOptions = {
  /** LaunchDarkly ``automatable-tickets-info`` (or camelCase ``automatableTicketsInfo``): when enabled, show per-step ``Theo tool: …`` lines from analytics. */
  showAutomatableTicketsInfo?: boolean;
  /** When true, onboarding/offboarding rows link to Custom Workflows (same gate as sidebar / ``CustomWorkflowsRoute``). */
  customWorkflowsNavVisible?: boolean;
};

/** Keys ``useFlags()`` may use for this flag (LD client often camelCases kebab-flag names). */
const AUTOMATABLE_TICKETS_INFO_LD_KEYS = ["automatable-tickets-info", "automatableTicketsInfo"] as const;

export function isAutomatableTicketsInfoLdEnabled(flags: Record<string, unknown>): boolean {
  for (const key of AUTOMATABLE_TICKETS_INFO_LD_KEYS) {
    const v = flags[key];
    if (v === true) return true;
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      if (s === "true" || s === "on" || s === "yes" || s === "1") return true;
    }
  }
  return false;
}

export const renderStepsTheoCell = (
  record: any,
  options?: AutomatableActionsCellOptions,
) => {
  const showToolHints = Boolean(options?.showAutomatableTicketsInfo);
  const showCustomWorkflowsLink =
    Boolean(options?.customWorkflowsNavVisible) && isUserOnboardingOrOffboardingAutomatableTicket(record);

  const raw = truncateText(getStepsTheoCanTake(record), 3500);
  const stepsEmpty = !raw || raw === "-";

  if (stepsEmpty && !showCustomWorkflowsLink) {
    return <Typography variant="body2">-</Typography>;
  }

  const lines = stepsEmpty ? [] : raw.split(/\n/).filter((line) => line.trim());
  const bindingsRaw = record?.analysis?.step_theo_action_bindings;
  const bindings: StepTheoActionBinding[] = Array.isArray(bindingsRaw) ? bindingsRaw : [];
  let bindingIdx = 0;

  const customWorkflowsHref = `${window.location.origin}${CUSTOM_WORKFLOWS_APP_PATH}`;

  const customWorkflowsCallout = showCustomWorkflowsLink ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        minWidth: 0,
        width: "100%",
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
        For user onboarding and offboarding, run Theo from{" "}
        <Link
          href={customWorkflowsHref}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ fontWeight: 600, color: "primary.main", fontSize: "inherit" }}
        >
          Custom Workflows
        </Link>
        .
      </Typography>
    </Box>
  ) : null;

  if (stepsEmpty) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, minWidth: 0, width: "100%" }}>
        {customWorkflowsCallout}
        <Typography variant="body2">-</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, minWidth: 0, width: "100%" }}>
      {customWorkflowsCallout}
      {lines.map((line, i) => {
        const withoutNumPrefix = line.replace(/^\s*\d+\.\s*/, "").trim();
        const tagMatch = withoutNumPrefix.match(/^(\[[^\]]+\])\s+(.+)$/s);
        if (tagMatch) {
          const [, bracketLabel, remainder] = tagMatch;
          const binding = bindings[bindingIdx++] ?? null;
          const toolLine =
            binding?.resolved_actions?.length ? binding.resolved_actions.join(", ") : "";
          const noteLine = binding?.resolution_note?.trim() || "";
          return (
            <Box
              key={i}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.35,
                alignItems: "stretch",
                minWidth: 0,
                width: "100%",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: "wrap", minWidth: 0, width: "100%" }}
              >
                <Chip
                  size="small"
                  label={bracketLabel}
                  sx={{
                    flexShrink: 0,
                    fontSize: "0.7rem",
                    height: "auto",
                    py: 0.25,
                    bgcolor: "#F1F5F9",
                    borderColor: "#CBD5E1",
                    color: "#0f172a",
                    fontWeight: 400,
                    "& .MuiChip-label": { fontWeight: 400 },
                  }}
                  variant="outlined"
                />
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ flex: "1 1 0", minWidth: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                >
                  {remainder}
                </Typography>
              </Box>
              {showToolHints && toolLine ? (
                <Typography
                  component="div"
                  variant="caption"
                  color="text.secondary"
                  sx={{ pl: { xs: 0, sm: 0.5 }, lineHeight: 1.45 }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Theo tool:{" "}
                  </Box>
                  {toolLine}
                </Typography>
              ) : null}
              {noteLine ? (
                <Typography
                  component="div"
                  variant="caption"
                  color="text.secondary"
                  sx={{ pl: { xs: 0, sm: 0.5 }, lineHeight: 1.45, fontStyle: toolLine ? "normal" : "italic" }}
                >
                  {noteLine}
                </Typography>
              ) : null}
            </Box>
          );
        }
        return (
          <Typography key={i} variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {line}
          </Typography>
        );
      })}
    </Box>
  );
};

/** Steps / actions column: optional "Execute with Theo" (prefills supervisor chat) plus step list. */
export const renderAutomatableActionsCell = (
  record: any,
  options?: AutomatableActionsCellOptions,
) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", minWidth: 0, width: "100%" }}>
    <AutomatableTheoExecuteLink record={record} />
    {renderStepsTheoCell(record, options)}
  </Box>
);

export const getTicketClientLabel = (ticket: any) =>
  ticket?.client_name ||
  ticket?.company_name ||
  getExcerptValue(ticket?.ticket_data_excerpt, [
    "client_name",
    "company_name",
    "account_name",
    "client.name",
    "company.name",
    "account.name",
    "client",
    "company",
    "account",
  ]) ||
  ticket?.client_id ||
  "-";

export const getTicketStatusLabel = (ticket: any) => {
  const direct = String(ticket?.status || ticket?.status_name || "").trim();
  if (direct) return direct;
  const fromExcerpt = getExcerptValue(ticket?.ticket_data_excerpt, [
    "status",
    "status_name",
    "ticket_status",
    "ticketStatus",
    "state",
    "phase",
    "ticket_status_label",
  ]);
  return String(fromExcerpt || "").trim() || "—";
};

export const getCurrentBucket = (record: any) => {
  const value = String(record?.analysis?.current_theo || "").toLowerCase();
  if (value === "yes") return "definitely_ai_can";
  if (value === "partial") return "ai_can_partly";
  return "";
};

export type AutomatableDashboardTableFilters = {
  search: string;
  scope: "all" | "full" | "partial";
  status: string;
  client: string;
};

/** Client-side filters for the dashboard automatable tickets table (full dataset already loaded). */
export const automatableDashboardRecordMatchesFilters = (
  record: any,
  filters: AutomatableDashboardTableFilters
): boolean => {
  if (filters.scope === "full" && getCurrentBucket(record) !== "definitely_ai_can") return false;
  if (filters.scope === "partial" && getCurrentBucket(record) !== "ai_can_partly") return false;

  const ticket = record?.ticket || {};
  const statusLabel = getTicketStatusLabel(ticket);
  if (filters.status && statusLabel !== filters.status) return false;

  const clientLabel = getTicketClientLabel(ticket);
  if (filters.client && clientLabel !== filters.client) return false;

  const q = filters.search.trim().toLowerCase();
  if (!q) return true;

  const tid = String(ticket.ticket_id ?? ticket.id ?? "").toLowerCase();
  const summary = String(ticket.problem_summary ?? "").toLowerCase();
  const category = String(record?.analysis?.ticket_type ?? "").toLowerCase();
  const haystack = `${tid} ${statusLabel.toLowerCase()} ${String(clientLabel).toLowerCase()} ${summary} ${category}`;
  return haystack.includes(q);
};

/** Category bucket for “By ticket category” rollups (matches ``dashboardAutomatableByCategory``). */
export const getAutomatableRecordCategory = (record: any): string => {
  const raw = String(record?.analysis?.ticket_type || "").trim();
  return raw || "Other";
};

/** Customer bucket for “By customer” rollups (matches ``dashboardAutomatableByCustomer``). */
export const getAutomatableRecordCustomerGroupLabel = (record: any): string => {
  const ticket = record?.ticket || {};
  let customer = getTicketClientLabel(ticket);
  if (!customer || customer.trim() === "" || customer === "-") {
    customer = "—";
  }
  return customer;
};

export const filterAutomatableRecordsByCategory = (records: any[], category: string): any[] =>
  records.filter((record) => getAutomatableRecordCategory(record) === category);

export const filterAutomatableRecordsByCustomer = (records: any[], customer: string): any[] =>
  records.filter((record) => getAutomatableRecordCustomerGroupLabel(record) === customer);

/** Fully or partially automatable under current Theo (dashboard Automatable Tickets panel). */
export const isAutomatableForDashboard = (
  record: any,
  options?: { legacyPreStepCoverage?: boolean },
) => {
  const b = getCurrentBucket(record);
  if (b !== "definitely_ai_can" && b !== "ai_can_partly") return false;
  if (options?.legacyPreStepCoverage) return true;
  return record?.analysis?.dashboard_automatable_eligible === true;
};

/** Rollup when grouping dashboard rows by client identity-cloud integration (M365 Graph and/or Google Workspace). */
export type CustomerM365RollupState = boolean | "mixed" | null;

export function mergeCustomerM365IntegrationState(
  prev: CustomerM365RollupState,
  next: boolean | null | undefined,
): CustomerM365RollupState {
  const n = typeof next === "boolean" ? next : null;
  if (prev === null) return n;
  if (prev === "mixed") return "mixed";
  if (n === null) return prev;
  if (prev === true && n === true) return true;
  if (prev === false && n === false) return false;
  return "mixed";
}

export function formatCustomerM365IntegrationLabel(state: CustomerM365RollupState): string {
  if (state === true) return "Yes";
  if (state === false) return "No";
  if (state === "mixed") return "Mixed";
  return "—";
}

/**
 * Whether the ticket's customer has Microsoft 365 (Graph) or Google Workspace actions enabled.
 * Yes if either flag is true. Legacy caches that only have ``m365_integration_available`` still work.
 */
export function cloudAppIntegrationAvailable(record: any): boolean | null {
  const m365 = record?.analysis?.m365_integration_available;
  const gw = record?.analysis?.gworkspace_integration_available;
  const m365b = typeof m365 === "boolean" ? m365 : null;
  const gwb = typeof gw === "boolean" ? gw : null;
  if (m365b === true || gwb === true) return true;
  if (m365b === false && gwb === false) return false;
  if (m365b === false && gwb === null) return false;
  if (gwb === false && m365b === null) return false;
  return null;
}

/** Rolling window for dashboard ticket query; must match server `buildDashboardAutomatableRequestDates`. */
export const DASHBOARD_AUTOMATABLE_LOOKBACK_DAYS = 14;

/** Must match server `mspAnalyticsRoutes` dashboard limit (shown in UI copy). */
export const DASHBOARD_AUTOMATABLE_TICKET_LIMIT = 1000;

/** Inclusive UTC calendar-day count from ISO instant strings (uses yyyy-mm-dd prefix only). */
export function inclusiveUtcCalendarSpanDaysFromIso(startIso: string, endIso: string): number | null {
  const ps = /^(\d{4})-(\d{2})-(\d{2})/.exec(startIso);
  const pe = /^(\d{4})-(\d{2})-(\d{2})/.exec(endIso);
  if (!ps || !pe) return null;
  const s = Date.UTC(Number(ps[1]), Number(ps[2]) - 1, Number(ps[3]));
  const e = Date.UTC(Number(pe[1]), Number(pe[2]) - 1, Number(pe[3]));
  if (e < s) return null;
  return Math.floor((e - s) / 86_400_000) + 1;
}

export function ticketLimitFromAnalyticsMetadata(metadata: Record<string, unknown> | undefined | null): number | null {
  if (!metadata || typeof metadata !== "object") return null;
  const n = metadata.limit;
  return typeof n === "number" && Number.isFinite(n) ? Math.trunc(n) : null;
}

function formatMetadataUtcDateLabel(iso: string): string | null {
  const day = iso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const d = new Date(`${day}T12:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return day;
  }
}

/** From cached `compute_msp_analytics_report` payload (`metadata`). Falls back constants when fields missing. */
export function deriveDashboardAutomatableLeadCopy(analytics: unknown): {
  inclusiveDaySpan: number;
  ticketLimit: number;
  /** e.g. "Apr 23, 2026 – May 6, 2026 UTC"; null when dates unusable */
  utcRangeLabel: string | null;
} {
  const fallback = {
    inclusiveDaySpan: DASHBOARD_AUTOMATABLE_LOOKBACK_DAYS,
    ticketLimit: DASHBOARD_AUTOMATABLE_TICKET_LIMIT,
  };
  let utcRangeLabel: string | null = null;
  if (!analytics || typeof analytics !== "object") {
    return { ...fallback, utcRangeLabel };
  }
  const meta = (analytics as { metadata?: unknown }).metadata;
  if (!meta || typeof meta !== "object") {
    return { ...fallback, utcRangeLabel };
  }
  const m = meta as Record<string, unknown>;
  const start = m.start_date;
  const end = m.end_date;
  let inclusiveDaySpan = fallback.inclusiveDaySpan;
  if (typeof start === "string" && typeof end === "string") {
    const span = inclusiveUtcCalendarSpanDaysFromIso(start, end);
    if (span != null && span >= 1) inclusiveDaySpan = span;
    const ls = formatMetadataUtcDateLabel(start.slice(0, 10));
    const le = formatMetadataUtcDateLabel(end.slice(0, 10));
    if (ls && le) utcRangeLabel = `${ls} – ${le} UTC`;
  }
  const limit = ticketLimitFromAnalyticsMetadata(m);
  const ticketLimit = limit != null && limit >= 1 ? limit : fallback.ticketLimit;
  return {
    inclusiveDaySpan,
    ticketLimit,
    utcRangeLabel,
  };
}

/** `yyyy-mm-dd` bounds from dashboard automatable cache `metadata` — pass into `UsageApi.getUsage` to match Automation Potential. */
export function getAutomatableMetadataDateInputsForUsage(analytics: unknown): {
  startDate: string;
  endDate: string;
} | null {
  if (!analytics || typeof analytics !== "object") return null;
  const meta = (analytics as { metadata?: unknown }).metadata;
  if (!meta || typeof meta !== "object") return null;
  const m = meta as Record<string, unknown>;
  const rawStart = m.start_date;
  const rawEnd = m.end_date;
  if (typeof rawStart !== "string" || typeof rawEnd !== "string") return null;
  const startDate = rawStart.trim().slice(0, 10);
  const endDate = rawEnd.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) return null;
  return { startDate, endDate };
}

/** Shown in sequence while the dashboard automatable request runs (client-side pacing; not live server events). */
export const DASHBOARD_AUTOMATABLE_LOADING_STEPS = [
  "Fetching tickets from the last 14 days…",
  "Bundling tickets for AI analysis…",
  "Running AI analysis on ticket batches (this is the slow part)…",
  "Scoring automation potential and Theo-ready steps…",
  "Building summaries and the automatable tickets table…",
] as const;

export const DASHBOARD_AUTOMATABLE_LOADING_STEP_MS = 7_500;

/** Extra “incremental log” lines while on the final loading step (timed; mirrors post-AI rollup work). */
export const DASHBOARD_AUTOMATABLE_FINAL_PHASE_LOG_INTERVAL_MS = 2_000;

export const DASHBOARD_AUTOMATABLE_FINAL_PHASE_LOG_MESSAGES = [
  "Rolling up solvability buckets (current vs theoretical)…",
  "Computing per-bucket counts and confidence totals…",
  "Aggregating ticket types and top missing capabilities…",
  "Building ticket landscape slices (flags, examples)…",
  "Deriving current & theoretical automation summaries…",
  "Scaling utilization / hours-saved style metrics…",
  "Merging batch results into full report records…",
  "Hydrating ticket payloads (summary, excerpt, status)…",
  "Projecting steps Theo can take and effort estimates…",
  "Filtering rows eligible for the automatable dashboard…",
  "Serializing the analytics payload for the app…",
] as const;
